import { useState, useEffect, useCallback } from 'react';
import { useServerSort, type ServerSortState } from '../../../hooks/useServerSort';
import { SelectChangeEvent } from '@mui/material';
import { InventoryRequestsService, LocationsService } from '../../../api';
import {
  mapApiRequestToDetail,
  mapApiRequestToRow,
  mapUiStatusToApiStatus,
  type InventoryRequestRow,
  type RequestDetail,
  type RequestDetailItem,
  type RequestFilterState,
  type RequestPaginationState,
} from '../../../utils/inventory/inventoryRequestMappers';
import type { NotificationSeverity } from '../../../utils/constants/notificationMessages';

export type {
  InventoryRequestApiStatus,
  InventoryRequestRow,
  RequestDetail,
  RequestDetailItem,
  RequestFilterState,
  RequestPaginationState,
} from '../../../utils/inventory/inventoryRequestMappers';

interface LocationOption {
  id: number;
  name: string;
}

const calculateTotalPages = (total: number, itemsPerPage: number) => {
  return Math.ceil(total / itemsPerPage);
};

const EMPTY_FILTERS: RequestFilterState = {
  requestCode: '',
  sourceLocation: '',
  destinationLocation: '',
  dateFrom: '',
  dateTo: '',
  status: '',
};

const DEFAULT_REQUEST_SORT: ServerSortState<InventoryRequestSortField> = {
  sortBy: 'createdAt',
  sortOrder: 'desc',
};

/** Sortable columns of the requests list (backend whitelist `INVENTORY_REQUEST_SORT_FIELDS`). */
export const INVENTORY_REQUEST_SORT_FIELDS = [
  'requestCode',
  'sourceLocationName',
  'destinationLocationName',
  'createdByName',
  'createdAt',
  'itemCount',
  'status',
] as const;

export type InventoryRequestSortField = (typeof INVENTORY_REQUEST_SORT_FIELDS)[number];

/** @param initialStatus Estado con el que arranca el filtro (p. ej. 'Pendiente' desde Inicio). */
export const useInventoryRequests = (initialStatus = '') => {
  const [requests, setRequests] = useState<InventoryRequestRow[]>([]);
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [filters, setFilters] = useState<RequestFilterState>(() => ({
    ...EMPTY_FILTERS,
    status: initialStatus,
  }));
  const [appliedFilters, setAppliedFilters] = useState<RequestFilterState>(() => ({
    ...EMPTY_FILTERS,
    status: initialStatus,
  }));
  const [pagination, setPagination] = useState<RequestPaginationState>({
    total: 0,
    currentPage: 1,
    itemsPerPage: 10,
    totalPages: 0,
  });
  // Orden de la tabla: lo resuelve el backend sobre todo el listado (no solo la página).
  const goToFirstPage = useCallback(
    () => setPagination((prev) => ({ ...prev, currentPage: 1 })),
    [],
  );
  const requestSort = useServerSort({
    fields: INVENTORY_REQUEST_SORT_FIELDS,
    defaultSort: DEFAULT_REQUEST_SORT,
    onSortChange: goToFirstPage,
  });
  const { resetSort: resetRequestSort } = requestSort;
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [searchTrigger, setSearchTrigger] = useState(0);

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedRequestDetail, setSelectedRequestDetail] = useState<RequestDetail | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: NotificationSeverity;
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const [statusSummary, setStatusSummary] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
  });
  const [statusSummaryLoading, setStatusSummaryLoading] = useState(false);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await LocationsService.locationsControllerFindAll();
        const list = response?.data ?? response;
        if (Array.isArray(list)) {
          setLocations(
            list.map((location: { id: number; name: string }) => ({
              id: Number(location.id),
              name: location.name,
            })),
          );
        }
      } catch (error) {
        console.error('Error al cargar ubicaciones:', error);
      }
    };

    fetchLocations();
  }, []);

  const fetchRequests = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await InventoryRequestsService.inventoryRequestsControllerFindAll(
        pagination.currentPage,
        pagination.itemsPerPage,
        appliedFilters.requestCode !== '' ? appliedFilters.requestCode : undefined,
        mapUiStatusToApiStatus(appliedFilters.status),
        appliedFilters.sourceLocation !== '' ? Number(appliedFilters.sourceLocation) : undefined,
        appliedFilters.destinationLocation !== ''
          ? Number(appliedFilters.destinationLocation)
          : undefined,
        appliedFilters.dateFrom !== '' ? appliedFilters.dateFrom : undefined,
        appliedFilters.dateTo !== '' ? appliedFilters.dateTo : undefined,
        requestSort.sortBy,
        requestSort.apiSortOrder,
      );

      // OpenAPI client already returns the body `{ data, meta }` — do not peel
      // `.data` before reading `meta` or total falls back to page length.
      const rows = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];
      const meta = response?.meta;

      const formatted = rows.map((row: Record<string, unknown>) => mapApiRequestToRow(row));
      setRequests(formatted);
      setPagination((prev) => ({
        ...prev,
        total: Number(meta?.total ?? formatted.length),
        currentPage: Number(meta?.page ?? prev.currentPage),
        totalPages: Number(
          meta?.totalPages ??
            calculateTotalPages(Number(meta?.total ?? formatted.length), prev.itemsPerPage),
        ),
      }));
    } catch (error) {
      console.error('Error al cargar solicitudes:', error);
      setRequests([]);
      setPagination((prev) => ({ ...prev, total: 0, totalPages: 0 }));
      setNotification({
        open: true,
        message: 'No se pudieron cargar las solicitudes de inventario.',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    appliedFilters.dateFrom,
    appliedFilters.dateTo,
    appliedFilters.destinationLocation,
    appliedFilters.requestCode,
    appliedFilters.sourceLocation,
    appliedFilters.status,
    pagination.currentPage,
    pagination.itemsPerPage,
    requestSort.sortBy,
    requestSort.apiSortOrder,
  ]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests, searchTrigger]);

  const handleFilterChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFilters((prev) => ({ ...prev, [name]: value }));
    },
    [],
  );

  const handleSelectChange = useCallback(
    (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
      const name = e.target.name as string;
      const value = e.target.value as string;
      setFilters((prev) => ({ ...prev, [name]: value }));
    },
    [],
  );

  const handleRequestCodeKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        setAppliedFilters(filters);
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
        setSearchTrigger((prev) => prev + 1);
      }
    },
    [filters],
  );

  const fetchStatusSummary = useCallback(async () => {
    setStatusSummaryLoading(true);
    try {
      const response =
        await InventoryRequestsService.inventoryRequestsControllerGetMetricsSummary();
      const pending = Number(response?.pending ?? 0);
      const approved = Number(response?.approved ?? 0);
      const rejected = Number(response?.rejected ?? 0);
      setStatusSummary({
        total: pending + approved + rejected,
        pending,
        approved,
        rejected,
      });
    } catch (error) {
      console.error('Error fetching inventory request status summary:', error);
    } finally {
      setStatusSummaryLoading(false);
    }
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setSearchTrigger((prev) => prev + 1);
    // Reiniciar también refresca el dashboard (p. ej. al volver a una pestaña ya abierta).
    void fetchStatusSummary();
  }, [fetchStatusSummary]);

  const handleSearch = useCallback(() => {
    setIsSearching(true);
    setAppliedFilters(filters);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setSearchTrigger((prev) => prev + 1);
    setIsSearching(false);
  }, [filters]);

  const clearAppliedFilterField = useCallback((field: keyof RequestFilterState) => {
    setAppliedFilters((prev) => {
      const next = { ...prev, [field]: '' };
      setFilters(next);
      return next;
    });
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setSearchTrigger((prev) => prev + 1);
  }, []);

  /** Deep-link from related entities: filter list by Código SOL and keep it after modal close. */
  const applyDeepLinkFilter = useCallback((requestCode: string) => {
    const nextFilters: RequestFilterState = { ...EMPTY_FILTERS, requestCode };
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setSearchTrigger((prev) => prev + 1);
  }, []);

  const applyStatusFilter = useCallback((status: string) => {
    setFilters((prev) => ({ ...prev, status }));
    setAppliedFilters((prev) => ({ ...prev, status }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setSearchTrigger((prev) => prev + 1);
  }, []);

  /** Clic desde Inicio: vista limpia (filtros, página y orden de fábrica) con solo ese estado. */
  const applyHomeStatusFilter = useCallback(
    (status: string) => {
      const homeFilters: RequestFilterState = { ...EMPTY_FILTERS, status };
      setFilters(homeFilters);
      setAppliedFilters(homeFilters);
      resetRequestSort();
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
      setSearchTrigger((prev) => prev + 1);
    },
    [resetRequestSort],
  );

  useEffect(() => {
    void fetchStatusSummary();
  }, [fetchStatusSummary]);

  const refreshAfterMutation = useCallback(async () => {
    await fetchRequests();
    void fetchStatusSummary();
  }, [fetchRequests, fetchStatusSummary]);

  const handlePageChange = useCallback((page: number) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
    setSearchTrigger((prev) => prev + 1);
  }, []);

  const handleItemsPerPageChange = useCallback((event: SelectChangeEvent<unknown>) => {
    const newItemsPerPage = Number(event.target.value);
    setPagination((prev) => ({
      ...prev,
      itemsPerPage: newItemsPerPage,
      currentPage: 1,
      totalPages: calculateTotalPages(prev.total, newItemsPerPage),
    }));
    setSearchTrigger((prev) => prev + 1);
  }, []);

  const toggleMoreFilters = useCallback(() => {
    setShowMoreFilters((prev) => !prev);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowDetailsModal(false);
    setSelectedRequestDetail(null);
  }, []);

  const handleViewDetails = useCallback(async (requestId: string) => {
    setIsLoadingDetails(true);
    setShowDetailsModal(true);

    try {
      const response = await InventoryRequestsService.inventoryRequestsControllerFindOne(
        Number(requestId),
      );
      const request = (response?.data ?? response) as Record<string, unknown>;
      setSelectedRequestDetail(mapApiRequestToDetail(request));
    } catch (error) {
      console.error('Error al cargar detalle de solicitud:', error);
      setSelectedRequestDetail(null);
      setNotification({
        open: true,
        message: 'No se pudo cargar el detalle de la solicitud.',
        severity: 'error',
      });
    } finally {
      setIsLoadingDetails(false);
    }
  }, []);

  const handleCloseNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  return {
    requests,
    locations,
    filters,
    appliedFilters,
    pagination,
    requestSort,
    isLoading,
    isSearching,
    showMoreFilters,
    showDetailsModal,
    selectedRequestDetail,
    isLoadingDetails,
    notification,
    statusSummary,
    statusSummaryLoading,
    handleFilterChange,
    handleSelectChange,
    handleRequestCodeKeyPress,
    handleResetFilters,
    handleSearch,
    clearAppliedFilterField,
    applyDeepLinkFilter,
    applyStatusFilter,
    applyHomeStatusFilter,
    handlePageChange,
    handleItemsPerPageChange,
    toggleMoreFilters,
    handleCloseModal,
    handleViewDetails,
    handleCloseNotification,
    fetchRequests,
    refreshAfterMutation,
    setNotification,
  };
};

export type ItemResolutionState = Record<
  number,
  {
    approved: boolean;
    rejectionReason: string;
  }
>;

export function buildInitialResolutionState(items: RequestDetailItem[]): ItemResolutionState {
  return items.reduce<ItemResolutionState>((acc, item) => {
    acc[item.id] = { approved: true, rejectionReason: '' };
    return acc;
  }, {});
}

export function buildResolvePayload(resolution: ItemResolutionState) {
  return {
    items: Object.entries(resolution).map(([id, value]) => ({
      id: Number(id),
      approved: value.approved,
      rejectionReason:
        !value.approved && value.rejectionReason.trim() ? value.rejectionReason.trim() : undefined,
    })),
  };
}
